import { ChevronRight, FileText, Users, Clock, Download } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MeetingHistoryService } from "@/features/Assistant/services/meetingHistoryService";
import type { Meeting, MeetingEvent, EventType, MeetingStatus } from "@/features/Assistant/types/meetingHistory";

interface MeetingCardProps {
  meeting: Meeting;
  isExpanded: boolean;
  onToggle: (sessionId: string) => void;
}

const getStatusBadge = (status: MeetingStatus) => {
  const badges: Record<MeetingStatus, string> = {
    delivered: "bg-green-500/15 text-green-500",
    in_progress: "bg-blue-500/15 text-blue-500",
    error: "bg-red-500/15 text-red-500",
    completed: "bg-gray-500/15 text-gray-500",
    scheduled: "bg-yellow-500/15 text-yellow-500",
  };
  return badges[status] || badges.completed;
};

const getEventTypeIcon = (eventType: EventType) => {
  const icons: Record<EventType, string> = {
    meeting_scheduled: "\uD83D\uDCC5",
    meeting_joined: "\uD83D\uDCDE",
    meeting_transcription: "\uD83D\uDCDD",
    meeting_left: "\uD83D\uDEAA",
    meeting_error: "\u274C",
  };
  return icons[eventType] || "\u2022";
};

const getEventDotColor = (eventType: EventType) => {
  if (eventType === "meeting_error") return "bg-red-500";
  if (eventType === "meeting_transcription") return "bg-green-500";
  return "bg-muted-foreground";
};

const formatTime = (isoString: string) => {
  const date = new Date(isoString);
  const userLocale = navigator.language || 'pt-BR';
  return date.toLocaleString(userLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimeShort = (isoString: string) => {
  const date = new Date(isoString);
  const userLocale = navigator.language || 'pt-BR';
  return date.toLocaleString(userLocale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const MeetingCard = ({ meeting, isExpanded, onToggle }: MeetingCardProps) => {
  const { t } = useTranslation();

  const getStatusLabel = (status: MeetingStatus) => t(`assistant.history.status.${status}`);

  const getEventTypeLabel = (eventType: EventType) => {
    const labels: Record<EventType, string> = {
      meeting_scheduled: "scheduled",
      meeting_joined: "joined",
      meeting_transcription: "transcription",
      meeting_left: "left",
      meeting_error: "error",
    };
    return t(`assistant.history.eventTypes.${labels[eventType]}`);
  };

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <button
        onClick={() => onToggle(meeting.sessionId)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors bg-muted/30"
      >
        <ChevronRight
          className={cn(
            "h-3 w-3 text-muted-foreground transition-transform",
            isExpanded && "rotate-90"
          )}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {meeting.meetingName || t("assistant.history.meeting")}
            </span>
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", getStatusBadge(meeting.latestStatus))}>
              {getStatusLabel(meeting.latestStatus)}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground">{formatTime(meeting.latestTime)}</span>
            {meeting.fromEmail && (
              <span className="text-xs text-muted-foreground truncate max-w-[250px]">
                {meeting.fromEmail}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {meeting.events.length} {t("assistant.history.events")}
            </span>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 border-t">
          <div className="relative pl-6">
            <div className="absolute left-2 top-1 bottom-1 w-px bg-border"></div>

            {meeting.events.map((evt: MeetingEvent) => (
              <div key={evt.id} className="relative mb-3 last:mb-0">
                <div className={cn("absolute -left-[18px] top-1 w-2.5 h-2.5 rounded-full", getEventDotColor(evt.event_type))}></div>

                <div className="flex items-start gap-2">
                  <span className="text-xs text-muted-foreground shrink-0">{formatTimeShort(evt.created_at)}</span>
                  <span className="text-xs">{getEventTypeIcon(evt.event_type)}</span>
                  <span className="text-xs font-medium">{getEventTypeLabel(evt.event_type)}</span>
                </div>

                {evt.event_type === "meeting_transcription" && evt.metadata && (
                  <div className="mt-1 ml-12 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {evt.metadata.segments_count != null && (
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {evt.metadata.segments_count} {t("assistant.history.segments")}
                      </span>
                    )}
                    {evt.metadata.participants_count != null && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {evt.metadata.participants_count} {t("assistant.history.participants")}
                      </span>
                    )}
                    {evt.metadata.duration_seconds != null && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t("assistant.history.duration")}: {MeetingHistoryService.formatDuration(evt.metadata.duration_seconds)}
                      </span>
                    )}
                    {evt.metadata.storage_path && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await MeetingHistoryService.downloadTranscript(
                              evt.metadata!.storage_path!
                            );
                          } catch {
                            toast.error(
                              t(
                                "assistant.history.downloadTranscriptError",
                                "Could not generate the download link."
                              )
                            );
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-primary hover:bg-primary/10 focus:outline-none"
                        title={t("assistant.history.downloadTranscript", "Baixar transcrição")}
                      >
                        <Download className="h-3 w-3" />
                        {t("assistant.history.downloadTranscript", "Baixar transcrição")}
                      </button>
                    )}
                  </div>
                )}

                {evt.event_type === "meeting_error" && (evt.error_message || evt.metadata?.error) && (
                  <div className="mt-1 ml-12 text-xs text-red-500">
                    {evt.error_message || evt.metadata?.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
